import { buttonVariants } from '@rouvydev/web-components/basics'
import { Suspense, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Await, Link, useLocation } from 'react-router'
import { type Route } from './+types/route'
import { Article } from '~/components/article.tsx'
import { Author } from '~/components/author'
import { Cols } from '~/components/cols.tsx'
import { DynamicZone } from '~/components/dynamic-zone/dynamic-zone.tsx'
import { GeneralErrorBoundary } from '~/components/error-boundary.tsx'
import { Gradient, Gradient4 } from '~/components/gradients.tsx'
import { Image } from '~/components/image.tsx'
import { Markdown } from '~/components/markdown.tsx'
import { NormalizedLink } from '~/components/normalized-link.tsx'
import { Separator } from '~/components/separator.tsx'
import { StructuredData } from '~/components/structured-data.tsx'
import { ErrorNotFound } from '~/components/ui/errors/error-not-found'
import {
  type BlogDynamicBodyFragment,
  type DynamicPageBodyFragment,
  type HomepageBodyFragment,
} from '~/model/api.ts'
import { getFormatter } from '~/utils/date-formatter.server.ts'
import { transformBody } from '~/utils/dynamic-zone.server.ts'
import { api } from '~/utils/gql-client.server.ts'
import { trackContentLayers } from '~/utils/gtm.client.ts'
import { markdown } from '~/utils/markdown.server.ts'
import { generateMeta, validateSeo, type MetaProps } from '~/utils/meta.ts'
import { invariant, invariantResponse } from '~/utils/misc.ts'
import { getPageState } from '~/utils/page-state.ts'

export function meta({ data }: Route.MetaArgs) {
  return data?.metaProps
    ? generateMeta(data.metaProps as MetaProps)
    : [{ title: '404 not found' }]
}

export let handle = {
  i18n: 'blog',
}

export async function loader({ request, params }: Route.LoaderArgs) {
  invariant(params.slug, 'slug not found')
  const pageState = await getPageState(request)

  const { blogPostBySlug } = await api.BlogPostsBySlug({
    slug: params.slug,
    locale: pageState.locale,
    publicationState: pageState.publicationState,
  })

  invariantResponse(blogPostBySlug, 'Blog post not found', { status: 404 })

  let authorBySlug
  if (blogPostBySlug.author?.data?.attributes?.slug) {
    const author = await api.AuthorBySlug({
      slug: blogPostBySlug.author?.data?.attributes?.slug,
    })

    authorBySlug = author.authorBySlug
  }

  const relatedPostsPromise = api.BlogPosts({
    start: 0,
    limit: 3,
    where: {
      $and: [
        { id: { $ne: blogPostBySlug.id } },
        blogPostBySlug.blog_category?.data?.id
          ? { blog_category: blogPostBySlug.blog_category.data.id }
          : {},
      ],
    },
  })

  const formatter = await getFormatter(request)
  const dateDisplay = formatter.format(new Date(blogPostBySlug.publishDate))
  const bodyDisplay = markdown(blogPostBySlug?.body ?? '')

  blogPostBySlug.dynamicBody = blogPostBySlug.dynamicBody?.filter(
    (component) => component !== null,
  )
  // @ts-ignore
  blogPostBySlug.dynamicBody = await transformBody(
    blogPostBySlug.dynamicBody as
      | HomepageBodyFragment[]
      | DynamicPageBodyFragment[]
      | BlogDynamicBodyFragment[],
    request,
  )

  const metaProps: MetaProps = {
    seo: await validateSeo(blogPostBySlug.SEO),
    localizations: blogPostBySlug?.localizations?.data.map(
      (a) => a.attributes?.locale,
    ) as string[],
    pageState: pageState,
  }

  return {
    metaProps: metaProps,
    relatedPostsPromise,
    blog: blogPostBySlug,
    dateDisplay,
    bodyDisplay,
    authorBySlug,
  }
}

export default function Blog({ loaderData }: Route.ComponentProps) {
  const { t } = useTranslation(['blog'])
  const { blog, dateDisplay, bodyDisplay, relatedPostsPromise, authorBySlug } =
    loaderData
  const { pathname } = useLocation()

  useEffect(() => {
    if (blog.contentLayers) {
      trackContentLayers(blog.contentLayers, pathname)
    }
  }, [blog.contentLayers, pathname])

  const structuredData: StructuredData = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: blog.structuredDataHeadline ?? blog.title,
    image: [blog?.cover?.data?.attributes?.url],
    datePublished: blog.publishDate,
    dateModified: blog.publishDate,
    author: [
      {
        '@type': 'Person',
        name: blog?.author?.data?.attributes?.name,
        url: `${ENV.BASE_URL}/blog/author/${blog?.author?.data?.attributes?.slug}`,
        image:
          blog?.author?.data?.attributes?.profilePhoto?.data[0]?.attributes
            ?.url,
      },
    ],
  }

  return (
    <>
      <div className="relative overflow-hidden py-32">
        <Gradient />

        {blog.cover?.data?.attributes?.url && (
          <div className="absolute top-[-130px] right-[-550px] md:top-[-150px] md:right-[-300px] lg:top-[-350px] lg:right-[-600px]">
            <Image
              src={blog.cover.data.attributes.url}
              alt={blog.title}
              className="hero-mask max-w-none object-cover opacity-[72%] md:h-[630px] md:w-[1200px] lg:h-[1280px] lg:w-[1920px]"
              formats={blog.cover.data.attributes.formats}
            />
          </div>
        )}

        <div className="relative z-10 container">
          <Cols size="8">
            <div className="flex flex-col gap-6">
              <p className="text-body-16 text-text-muted">
                {dateDisplay}
                {blog.author?.data?.attributes?.name && (
                  <>
                    {' \u2013 '}
                    <Link
                      to={`/blog/author/${blog.author.data.attributes.slug}`}
                      className="underline hover:no-underline"
                    >
                      {blog.author.data.attributes.name}
                    </Link>
                  </>
                )}
              </p>
              <h1 className="font-display text-display-40 leading-tight font-extrabold text-text-default uppercase italic">
                {blog.title}
              </h1>
            </div>
          </Cols>
        </div>
      </div>

      <div className="container">
        <Cols size="8" className="py-24">
          <p className="text-body-20 text-text-muted">{blog.perex}</p>
          <Separator className="my-12" />
          <Article>
            <Markdown content={bodyDisplay} />
          </Article>
        </Cols>
      </div>

      <DynamicZone
        unionMembers={blog.dynamicBody as BlogDynamicBodyFragment[]}
      />

      {authorBySlug && (
        <>
          <Separator className="mt-24" />
          <div className="container">
            <Cols size="8" className="py-24">
              <Author
                name={authorBySlug.name}
                avatar={authorBySlug.profilePhoto?.data[0].attributes?.url}
                jobTitle={authorBySlug.jobTitle}
                socials={authorBySlug.socialPlatform}
                perex={authorBySlug.perex}
                authorSlug={authorBySlug.slug}
              />
            </Cols>
          </div>
        </>
      )}

      <Suspense
        fallback={
          <p className="p-10 text-center text-body-16 text-text-default">
            {t('loading_posts')}
          </p>
        }
      >
        <Await resolve={relatedPostsPromise}>
          {({ blogPosts }) => {
            if (!blogPosts.length) {
              return null
            }

            return (
              <div className="relative overflow-hidden">
                <Gradient4 />
                <div className="absolute top-[calc(50%-1347px+169px)] left-[calc(50%-2400px/2-350px)] h-[2645px] w-[2400px] bg-pattern-2 bg-no-repeat" />
                <div className="relative z-10 container">
                  <Cols size="8" className="py-20">
                    <h3 className="text-center font-display text-[4.5rem] leading-none text-text-default uppercase italic">
                      {t('related_posts')}
                    </h3>
                    {blogPosts.length > 0 && (
                      <div className="grid grid-cols-1 gap-8 pt-16 xl:grid-cols-3">
                        {blogPosts.map((post) => (
                          <div key={post.id} className="bg-background-default">
                            {post.cover?.data?.attributes?.url && (
                              <Image
                                src={post.cover.data.attributes?.url}
                                alt={
                                  post.cover.data.attributes.alternativeText ??
                                  ''
                                }
                                formats={post.cover.data.attributes.formats}
                                width={350}
                                height={350}
                                className="aspect-[16/9] w-full object-cover sm:aspect-[2/1] lg:aspect-[3/2] lg:h-[320px]"
                              />
                            )}
                            <div className="flex flex-col gap-6 p-8">
                              <h3 className="line-clamp-2 font-display text-body-32 leading-8 font-extrabold text-text-default uppercase italic">
                                <NormalizedLink to={`/blog/${post.slug}`}>
                                  {post.title}
                                </NormalizedLink>
                              </h3>
                              <p className="line-clamp-4 text-body-16 text-text-muted">
                                {post.perex}
                              </p>
                              <p>
                                <NormalizedLink
                                  className={buttonVariants({
                                    size: 'medium',
                                    variant: 'alternative',
                                  })}
                                  to={`/blog/${post.slug}`}
                                >
                                  {t('read_article_link')}
                                </NormalizedLink>
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Cols>
                </div>
              </div>
            )
          }}
        </Await>
      </Suspense>
      <StructuredData structuredData={structuredData} />
    </>
  )
}

export function ErrorBoundary() {
  const { t } = useTranslation(['blog'])
  return (
    <GeneralErrorBoundary
      statusHandlers={{
        404: () => <ErrorNotFound description={t('blog_post_not_found')} />,
      }}
    />
  )
}
